import React, { useState, useRef, useEffect } from "react";
import SunEditor from "suneditor-react";
import "suneditor/dist/css/suneditor.min.css";
import Select, { components } from "react-select";
import EntityConstants from "../../configs/EntityConstants";
import { GET, POST, PUT } from "../../service/CRUDService";
import Toast from "../../common/Toast/Toast";
import CorrectIcon from "../../resources/icons/correct-green.svg";
import SimpleModal from "../../common/SimpleModal/SimpleModal";
import WarningIcon from "../../resources/icons/blue-warning.svg";
import { emailccRegex } from "../../configs/App.Regex";
import { IoCloseCircleOutline } from "react-icons/io5";
import { useTranslation } from "react-i18next";
import CloneEmailTemplateModal from "../../common/CloneEmailTemplateModal/CloneEmailTemplateModal";
import { pageRootTestIds } from "../../testing/pageRootTestIds";
import {
  createBlockLocalImageUploadBefore,
  getEmailTemplateSunEditorOptions,
} from "./emailTemplateSunEditorOptions";

const TEMPLATE_PAGE_LIMIT = 100;

const EmailTemplateSelectOption = (props) => (
  <components.Option
    {...props}
    innerProps={{
      ...props.innerProps,
      id: `email-template-editor-template-option-${props.data.value}`,
    }}
  />
);

const EmailTemplateEditor = ({ onCloneTemplateClick }) => {
  const sunEditorRef = useRef(null);
  const pendingEditorContentRef = useRef(null);
  const ccInputRef = useRef(null);
  const { t } = useTranslation();

  const [emailSubject, setEmailSubject] = useState("");
  const [editorContent, setEditorContent] = useState("");
  const [initialSubject, setInitialSubject] = useState("");
  const [initialContent, setInitialContent] = useState("");
  const [isDataChanged, setIsDataChanged] = useState(false);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [emailCCArray, setEmailCCArray] = useState([]);
  const [currentEmailCC, setCurrentEmailCC] = useState("");
  const [emailCCError, setEmailCCError] = useState("");
  const [initialEmailCC, setInitialEmailCC] = useState([]);
  const [isCloneModalOpen, setIsCloneModalOpen] = useState(false);
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const blockLocalImageUploadBefore = createBlockLocalImageUploadBefore({
    t,
    Toast,
  });

  const handleSubjectChange = (e) => {
    setEmailSubject(e.target.value);
  };

  const handleEditorChange = (content) => {
    setEditorContent(content);
  };

  const handleEmailCCChange = (e) => {
    const email = e.target.value;
    setCurrentEmailCC(email);
    // Clear error if the email is valid or empty
    if (!email || emailccRegex.test(email)) {
      setEmailCCError("");
    }
  };

  const handleAddEmailCC = () => {
    if (emailccRegex.test(currentEmailCC)) {
      if (emailCCArray.includes(currentEmailCC)) {
        setEmailCCError(`* ${t("history:email_already_exists")}`);
      } else {
        setEmailCCArray([...emailCCArray, currentEmailCC]);
        setCurrentEmailCC("");
        setEmailCCError("");
      }
    } else if (currentEmailCC) {
      setEmailCCError(`* ${t("settings:please_enter_valid_email")}`);
    }
  };

  const handleRemoveEmailCC = (email) => {
    setEmailCCArray(emailCCArray.filter((item) => item !== email));
    setEmailCCError("");
  };

  const handlePasteEmailCC = (event) => {
    event.preventDefault();
    const pastedText = event.clipboardData.getData("text");
    const emailMatches = pastedText.match(emailccRegex) || [];
    const uniqueEmails = [
      ...new Set(emailMatches.map((email) => email.trim())),
    ];

    uniqueEmails.forEach((email) => {
      if (emailCCArray.includes(email)) {
        setEmailCCError(
          `* ${t("settings:email")} "${email}" ${t("history:already_exists")}`,
        );
      } else {
        setEmailCCArray((prev) => [...prev, email]);
      }
    });

    const invalidEmails = pastedText
      .split(/[,\s]+/)
      .map((email) => email.trim())
      .filter(
        (email) =>
          email && !emailccRegex.test(email) && !uniqueEmails.includes(email),
      );

    if (invalidEmails.length > 0) {
      setEmailCCError(
        `${t(
          "settings:the_following_emails_are_invalid",
        )}: ${invalidEmails.join(", ")}`,
      );
    } else {
      setEmailCCError("");
    }

    setCurrentEmailCC("");
  };

  const saveContent = async () => {
    if (
      emailSubject !== initialSubject ||
      editorContent !== initialContent ||
      JSON.stringify(emailCCArray) !== JSON.stringify(initialEmailCC)
    ) {
      const response = await POST(EntityConstants.EMAIL_TEMPLATES, {
        template_id: selectedTemplate.template_id,
        email_subject: emailSubject,
        email_body: editorContent,
        email_cc: emailCCArray,
      });
      if (response.ok) {
        const data = await response.json();
        setInitialSubject(emailSubject);
        setInitialContent(editorContent);
        setInitialEmailCC(emailCCArray);
        Toast({
          type: "success",
          title: t("settings:success"),
          message: data.message,
        });
      } else {
        const errorData = await response.json();
        Toast({
          type: "error",
          title: t("settings:error"),
          message: errorData.error,
        });
      }
    } else {
      console.log("No changes to save");
    }
  };

  const applyEditorContent = (htmlContent, { updateInitial = false } = {}) => {
    setEditorContent(htmlContent);
    pendingEditorContentRef.current = htmlContent;
    if (updateInitial) {
      setInitialContent(htmlContent);
    }

    if (
      sunEditorRef.current &&
      typeof sunEditorRef.current.setContents === "function"
    ) {
      sunEditorRef.current.setContents(htmlContent);
    }
  };

  const insertVariable = (variable) => {
    const placeholder = `{{${variable}}}`;
    if (
      sunEditorRef.current &&
      typeof sunEditorRef.current.insertHTML === "function"
    ) {
      sunEditorRef.current.insertHTML(placeholder);
    }
  };

  const getSunEditorInstance = (sunEditor) => {
    sunEditorRef.current = sunEditor;
    if (!sunEditor) return;

    const pendingContent = pendingEditorContentRef.current;
    if (
      pendingContent != null &&
      typeof sunEditor.setContents === "function"
    ) {
      sunEditor.setContents(pendingContent);
    }
  };

  const fetchTemplatesPage = async () => {
    const response = await GET(
      `${EntityConstants.EMAIL_TEMPLATES}?limit=${TEMPLATE_PAGE_LIMIT}&offset=0`,
    );
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data?.error || t("settings:error_fetching_data"));
    }

    const templateList = data?.results || data?.email_templates || [];
    return {
      templates: templateList,
    };
  };

  const fetchEmailTemplate = async ({ isMounted = () => true } = {}) => {
    try {
      setIsLoadingTemplates(true);

      const pageData = await fetchTemplatesPage();
      if (!isMounted()) return;

      const loadedTemplates = pageData.templates || [];

      setEmailTemplates(loadedTemplates);

      const activeTemplate =
        loadedTemplates.find((template) => template.is_active) ||
        loadedTemplates[0] ||
        null;

      if (activeTemplate) {
        setSelectedTemplate(activeTemplate);
        const htmlResponse = await fetch(activeTemplate.html_file_path);
        if (!isMounted()) return;

        if (htmlResponse.ok) {
          const blob = await htmlResponse.blob();
          const htmlContent = await blob.text();

          if (!isMounted()) return;

          setEmailSubject(activeTemplate.email_subject);
          setInitialSubject(activeTemplate.email_subject);
          setEmailCCArray(activeTemplate.email_cc || []);
          setInitialEmailCC(activeTemplate.email_cc || []);
          applyEditorContent(htmlContent, { updateInitial: true });
        } else {
          Toast({
            type: "error",
            title: t("settings:error"),
            message: t("settings:failed_fetch_html_s3"),
          });
        }
      }
    } catch (error) {
      if (!isMounted()) return;

      console.error("Error fetching email template:", error);
      Toast({
        type: "error",
        title: t("settings:error"),
        message: t("settings:error_fetching_email_template"),
      });
    } finally {
      if (isMounted()) {
        setIsLoadingTemplates(false);
      }
    }
  };

  const handleTemplateChange = async (selectedOption) => {
    const template = emailTemplates.find(
      (tmpl) => tmpl.template_id === selectedOption.value,
    );
    setSelectedTemplate(template);

    if (template) {
      setEmailSubject(template.email_subject);
      setEmailCCArray(template.email_cc || []);
      setInitialEmailCC(template.email_cc || []);

      try {
        const htmlResponse = await fetch(template.html_file_path);
        if (htmlResponse.ok) {
          const blob = await htmlResponse.blob();
          const htmlContent = await blob.text();

          applyEditorContent(htmlContent);
        } else {
          Toast({
            type: "error",
            title: t("settings:error"),
            message: t("settings:failed_fetch_email_template_content"),
          });
        }
      } catch (error) {
        console.error("Error fetching email template content:", error);
        Toast({
          type: "error",
          title: t("settings:error"),
          message: t("settings:error_fetching_email_template_content"),
        });
      }
    }
  };

  const activateTemplate = async () => {
    if (selectedTemplate) {
      const response = await PUT(EntityConstants.EMAIL_TEMPLATES, {
        template_id: selectedTemplate.template_id,
        is_active: true,
      });

      if (response.ok) {
        setIsModalOpen(false);
        const data = await response.json();

        setEmailSubject(data.template.email_subject);
        setInitialSubject(data.template.email_subject);
        setEmailCCArray(data.template.email_cc || []);
        setInitialEmailCC(data.template.email_cc || []);

        try {
          const htmlResponse = await fetch(data.template.html_file_path);
          if (htmlResponse.ok) {
            const blob = await htmlResponse.blob();
            const htmlContent = await blob.text();

            applyEditorContent(htmlContent, { updateInitial: true });
          } else {
            Toast({
              type: "error",
              title: t("settings:error"),
              message: t("settings:failed_fetch_updated_html"),
            });
          }
        } catch (error) {
          console.error("Error fetching updated email content:", error);
          Toast({
            type: "error",
            title: t("settings:error"),
            message: t("settings:error_fetching_updated_email_content"),
          });
        }

        Toast({
          type: "success",
          title: t("settings:success"),
          message: data.message,
        });
        fetchEmailTemplate();
      } else {
        const errorData = await response.json();
        Toast({
          type: "error",
          title: t("settings:error"),
          message: errorData.error,
        });
      }
    }
  };

  const handleCloneTemplate = async () => {
    if (onCloneTemplateClick) {
      onCloneTemplateClick(selectedTemplate?.template_id || "");
      return;
    }

    try {
      setAvailableTemplates(emailTemplates);
      setIsCloneModalOpen(true);
    } catch (error) {
      console.error("Error fetching email templates for clone:", error);
      Toast({
        type: "error",
        title: t("settings:error"),
        message: t("settings:error_fetching_email_templates_clone"),
      });
    }
  };

  const handleCloneSubmit = async (templateId, templateName, emailSubject) => {
    try {
      const response = await POST(EntityConstants.EMAIL_TEMPLATE_CLONE, {
        template_id: templateId,
        template_name: templateName,
        email_subject: emailSubject,
      });

      if (response.ok) {
        const data = await response.json();
        Toast({
          type: "success",
          title: t("settings:success"),
          message: data.message || t("settings:email_template_cloned_success"),
        });
        setIsCloneModalOpen(false);
        fetchEmailTemplate();
      } else {
        const errorData = await response.json();
        Toast({
          type: "error",
          title: t("settings:error"),
          message: errorData.error || t("settings:failed_clone_email_template"),
        });
      }
    } catch (error) {
      console.error("Error cloning template:", error);
      Toast({
        type: "error",
        title: t("settings:error"),
        message: t("settings:failed_clone_email_template"),
      });
    }
  };

  useEffect(() => {
    let isMounted = true;

    fetchEmailTemplate({ isMounted: () => isMounted });

    return () => {
      isMounted = false;
      sunEditorRef.current = null;
    };
  }, []);

  useEffect(() => {
    const customDropdownButton = document.createElement("select");
    customDropdownButton.id = "email-template-editor-variable-select";
    customDropdownButton.innerHTML = ` 
      <option value="">${t("settings:variables")}</option>
      <option value="first_name">${t("settings:first_name")}</option>
      <option value="last_name">${t("settings:last_name")}</option>
      <option value="org_name">${t("settings:organisation_name")}</option>
    `;
    customDropdownButton.style.cssText =
      "width: 100px; margin-left: 5px; margin-bottom: 3px; padding: 5px 10px; font-size: 14px;";

    customDropdownButton.addEventListener("change", (e) => {
      const selectedValue = e.target.value;
      if (selectedValue) {
        insertVariable(selectedValue);
      }
    });

    const toolbarElement = document.querySelector(".se-toolbar");
    if (toolbarElement) {
      toolbarElement.appendChild(customDropdownButton);
    }

    return () => {
      if (toolbarElement) {
        toolbarElement.removeChild(customDropdownButton);
      }
    };
  }, []);

  useEffect(() => {
    const editable = document.querySelector(
      "#email-template-editor-body .sun-editor-editable",
    );
    if (editable) {
      editable.id = "email-template-editor-body-input";
    }
  }, [editorContent, selectedTemplate]);

  useEffect(() => {
    if (
      emailSubject !== initialSubject ||
      editorContent !== initialContent ||
      JSON.stringify(emailCCArray) !== JSON.stringify(initialEmailCC)
    ) {
      setIsDataChanged(true);
    } else {
      setIsDataChanged(false);
    }
  }, [
    emailSubject,
    editorContent,
    initialSubject,
    initialContent,
    selectedTemplate,
    emailCCArray,
    initialEmailCC,
  ]);

  const customStyles = {
    menuPortal: (base) => ({
      ...base,
      zIndex: 9,
    }),
    option: (provided, { data }) => ({
      ...provided,
      backgroundColor: data.is_active ? "white" : "white",
      color: data.is_active ? "black" : "black",
    }),
  };

  const templateOptions = emailTemplates.map((template) => ({
    value: template.template_id,
    label: (
      <div className="flex items-center">
        {template.template_name}{" "}
        {template.is_active && (
          <div className="flex ml-2 px-[12px] py-[2px] bg-cfActiveBg text-cfActiveText rounded-[99px] border border-cfActiveBorder items-center">
            <img src={CorrectIcon} className="mr-1 h-4 w-4" alt="active" />
            {t("settings:active")}
          </div>
        )}
      </div>
    ),
    is_active: template.is_active,
  }));

  return (
    <div className="mx-auto p-3" id={pageRootTestIds.emailTemplateEditor}>
      <div className="mx-auto p-3 border border-gray-300 rounded-md">
        <div className="text-xl font-semibold text-gray-900 mb-4">
          <p>{t("settings:email_template_editor")}</p>
        </div>
        <div className="flex items-end gap-4">
          <div className="flex-1">
            <label
              className="text-sm font-medium text-gray-900"
              htmlFor="email-template-editor-template-select"
            >
              {t("settings:email_template")}
            </label>
            <Select
              options={templateOptions}
              styles={customStyles}
              onChange={handleTemplateChange}
              instanceId="email-template-editor-template-select"
              inputId="email-template-editor-template-select"
              components={{ Option: EmailTemplateSelectOption }}
              value={templateOptions.find(
                (option) => option.value === selectedTemplate?.template_id,
              )}
              menuPortalTarget={document.body}
              isLoading={isLoadingTemplates}
            />
          </div>
          <div className="flex gap-2 items-center">
            {selectedTemplate && (
              <>
                <button
                  id="email-template-editor-clone"
                  className="px-[1.7rem] py-[0.6rem] bg-cfPrimaryButton text-cfSecondary font-semibold text-base font-CfPrimaryFont rounded-md border-none transition-all hover:bg-cfPrimaryDark cursor-pointer"
                  onClick={handleCloneTemplate}
                >
                  {t("settings:clone_template")}
                </button>

                {selectedTemplate.is_active ? (
                  <button
                    id="email-template-editor-active-template-button"
                    className="px-[1.55rem] py-[0.6rem] cursor-not-allowed focus:outline-none bg-[#868688] text-cfSecondary font-semibold text-base font-CfPrimaryFont rounded-md border-none transition-all"
                  >
                    {t("settings:active_template")}
                  </button>
                ) : (
                  <button
                    id="email-template-editor-use-template"
                    className="px-6 py-[0.6rem] bg-cfPrimaryButton text-cfSecondary font-semibold text-base font-CfPrimaryFont rounded-md border-none transition-all hover:bg-cfPrimaryDark"
                    onClick={() => setIsModalOpen(true)}
                  >
                    {t("settings:use_template")}
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        <div className="email-cc my-4">
          <label
            className="text-sm font-medium text-gray-900"
            htmlFor="email-cc"
          >
            {t("settings:cc_emails")}
          </label>
          <div
            role="button"
            tabIndex={0}
            className="flex border-[1.5px] border-gray-300 rounded-lg w-full cursor-text"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                ccInputRef.current?.focus();
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                if (e.target === e.currentTarget) {
                  ccInputRef.current?.focus();
                }
              }
            }}
          >
            <div
              role="button"
              tabIndex={0}
              className="flex items-start gap-2 flex-wrap px-2 py-1 bg-cfSecondary w-full"
              onClick={(e) => {
                e.stopPropagation();
                ccInputRef.current?.focus();
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.stopPropagation();
                  ccInputRef.current?.focus();
                }
              }}
            >
              {emailCCArray.map((email) => (
                <div
                  key={email}
                  className="bg-cfPrimary hover:bg-cfPrimaryDark text-cfSecondary px-2 py-1 rounded-full flex gap-1 items-center"
                >
                  {email}
                  <button
                    className="flex items-center justify-center rounded-full text-lg h-6 w-6 outline-none focus:outline-none focus:ring-0"
                    onClick={() => handleRemoveEmailCC(email)}
                  >
                    <IoCloseCircleOutline size={20} />
                  </button>
                </div>
              ))}
              <div className="w-fit flex gap-2 items-center">
                <input
                  ref={ccInputRef}
                  type="email"
                  id="email-cc"
                  placeholder="john.doe@gmail.com"
                  value={currentEmailCC}
                  onChange={handleEmailCCChange}
                  onKeyDown={(e) => e.key === "Enter" && handleAddEmailCC()}
                  onPaste={handlePasteEmailCC}
                  className={`${
                    currentEmailCC ? "border-2 border-cfPrimary" : "border-0"
                  } focus:border-2 focus:border-cfPrimary rounded-full focus:outline-cfPrimary text-gray-700 ${
                    !currentEmailCC
                      ? "placeholder:text-gray-300"
                      : "placeholder:text-gray-400"
                  } px-2 py-1`}
                />
                {!emailCCError && currentEmailCC && (
                  <button
                    className="rounded-full bg-cfPrimary hover:bg-cfPrimaryDark text-cfSecondary px-2 py-1"
                    onClick={handleAddEmailCC}
                  >
                    +{t("settings:add")}
                  </button>
                )}
              </div>
            </div>
          </div>
          {emailCCError && (
            <p className="text-red-500 mt-1 text-sm">{emailCCError}</p>
          )}
        </div>

        {/* Email Subject */}
        <div className="email-subject mt-4 mb-4">
          <label
            className="text-sm font-medium text-gray-900"
            htmlFor="email-subject"
          >
            {t("settings:email_subject")}
          </label>
          <input
            className="w-full p-2 text-base text-gray-700 rounded-md border border-gray-300"
            type="text"
            id="email-subject"
            value={emailSubject}
            onChange={handleSubjectChange}
            placeholder={t("settings:enter_email_subject")}
          />
        </div>

        {/* Email Body */}
        <div className="email-body mb-5">
          <label
            className="text-sm font-medium text-gray-900"
            htmlFor="email-body"
          >
            {t("settings:email_body")}
          </label>
          <div id="email-template-editor-body">
            <SunEditor
              getSunEditorInstance={getSunEditorInstance}
              setOptions={getEmailTemplateSunEditorOptions({ height: "50vh" })}
              onImageUploadBefore={blockLocalImageUploadBefore}
              onChange={handleEditorChange}
              value={editorContent}
            />
          </div>
        </div>

        {/* Save Button */}
        <button
          id="email-template-editor-save-button"
          disabled={!isDataChanged}
          className={`relative inline-flex items-center justify-start px-6 md:px-12 py-2 overflow-hidden font-medium ${
            isDataChanged
              ? "bg-cfPrimary hover:bg-cfPrimaryDark"
              : "bg-gray-400"
          } text-cfSecondary rounded-md border-none transition-all`}
          onClick={saveContent}
        >
          {t("settings:save_changes")}
        </button>
      </div>

      <SimpleModal
        isOpen={isModalOpen}
        closeModal={() => setIsModalOpen(false)}
        successFunction={activateTemplate}
        modalIcon={WarningIcon}
        leftButtonMessage={t("settings:cancel")}
        rightButtonMessage={t("settings:confirm")}
        modalHeaderMessage={t("settings:confirm_template_activation")}
        modalBodyMessage={`${t("settings:template_notification_message")}`}
        testId="email-template-editor-activate-modal"
      />

      <CloneEmailTemplateModal
        isOpen={isCloneModalOpen}
        onClose={() => setIsCloneModalOpen(false)}
        templates={availableTemplates}
        onSubmit={handleCloneSubmit}
      />
    </div>
  );
};

export default EmailTemplateEditor;
